import { groupIntoMeasures, getBeatsPerMeasure, BEAT_VALUES } from './musicTheory.js'

const XML_ALTER_BY_ACC = { '#': 1, '##': 2, b: -1, bb: -2 }

export function pitchToXML(pitch) {
  const m = pitch.match(/^([A-G])(##|#|bb|b|n)?(\d)$/)
  if (!m) return ''
  const [, step, acc, octave] = m
  const alterVal = XML_ALTER_BY_ACC[acc]
  const alter = alterVal != null ? `<alter>${alterVal}</alter>` : ''
  return `<pitch><step>${step}</step>${alter}<octave>${octave}</octave></pitch>`
}

/** MusicXML fifths → 앱 Key */
const FIFTHS_TO_KEY = {
  0: 'C', 1: 'G', 2: 'D', 3: 'A', 4: 'E', 5: 'B', 6: 'F#',
  [-1]: 'F', [-2]: 'Bb', [-3]: 'Eb', [-4]: 'Ab', [-5]: 'Db',
}
/** 앱 Key → MusicXML fifths */
const KEY_TO_FIFTHS = {
  C: 0, G: 1, D: 2, A: 3, E: 4, B: 5, 'F#': 6,
  F: -1, Bb: -2, Eb: -3, Ab: -4, Db: -5,
}

function fifthsToKeyString(fifths) {
  return FIFTHS_TO_KEY[fifths] ?? 'C'
}

// 단조 으뜸음 pc (Toolbar.jsx의 KEY_SIGNATURES_MINOR와 동일 12개, 표기 일관성 유지)
const MINOR_TONIC_PC = {
  Cm: 0, Gm: 7, Dm: 2, Am: 9, Em: 4, Bm: 11, 'F#m': 6, Fm: 5, Bbm: 10, Ebm: 3, Abm: 8, Dbm: 1,
}
const PC_TO_MINOR_KEY = Object.fromEntries(Object.entries(MINOR_TONIC_PC).map(([k, v]) => [v, k]))
// 장조 으뜸음 pc — 단조 fifths 계산(친척장조 = 으뜸음+3반음)과 왕복 변환에 사용
const MAJOR_TONIC_PC = { C: 0, G: 7, D: 2, A: 9, E: 4, B: 11, 'F#': 6, F: 5, Bb: 10, Eb: 3, Ab: 8, Db: 1 }

/** 앱 Key(장조 "C" 또는 단조 "Am") → { fifths, mode } — <key><fifths>/<mode> 작성용. */
function keyToFifthsAndMode(keySignature) {
  const k = String(keySignature ?? 'C')
  if (k in MINOR_TONIC_PC) {
    const relMajorPc = (MINOR_TONIC_PC[k] + 3) % 12
    const majorLetter = Object.keys(MAJOR_TONIC_PC).find((l) => MAJOR_TONIC_PC[l] === relMajorPc) ?? 'C'
    return { fifths: KEY_TO_FIFTHS[majorLetter] ?? 0, mode: 'minor' }
  }
  return { fifths: KEY_TO_FIFTHS[k] ?? 0, mode: 'major' }
}

/** <key><fifths>/<mode> → 앱 Key. mode가 없거나 "major"면 장조로 읽는다(구버전 파일과 호환). */
function fifthsAndModeToKeyString(fifths, mode) {
  const majorLetter = fifthsToKeyString(fifths)
  if (mode !== 'minor') return majorLetter
  const majorPc = MAJOR_TONIC_PC[majorLetter] ?? 0
  const minorPc = (majorPc - 3 + 12) % 12
  return PC_TO_MINOR_KEY[minorPc] ?? 'Am'
}

function typeToDuration(typeName) {
  const t = (typeName || '').toLowerCase()
  const map = { whole: 'w', half: 'h', quarter: 'q', eighth: '8', sixteenth: '16' }
  return map[t] ?? 'q'
}

function divisionsTicksToDuration(ticks, divisions) {
  if (!divisions || divisions < 1) divisions = 4
  const beats = ticks / divisions
  const tol = 0.02
  if (Math.abs(beats - 4) < tol) return 'w'
  if (Math.abs(beats - 2) < tol) return 'h'
  if (Math.abs(beats - 1) < tol) return 'q'
  if (Math.abs(beats - 0.5) < tol) return '8'
  if (Math.abs(beats - 0.25) < tol) return '16'
  return 'q'
}

function toRestKey(noteDur) {
  const map = { w: 'wr', h: 'hr', q: 'qr', '8': '8r', '16': '16r' }
  return map[noteDur] ?? 'qr'
}

function parsePitchFromNote(noteEl) {
  const p = noteEl.querySelector('pitch')
  if (!p) return null
  const step = p.querySelector('step')?.textContent?.trim()
  const octave = p.querySelector('octave')?.textContent?.trim()
  if (!step || octave === undefined) return null
  const alter = parseInt(p.querySelector('alter')?.textContent ?? '0', 10) || 0
  const accidentalText = noteEl.querySelector('accidental')?.textContent?.trim().toLowerCase()
  let acc = ''
  if (alter === 1) acc = '#'
  else if (alter === 2) acc = '##'
  else if (alter === -1) acc = 'b'
  else if (alter === -2) acc = 'bb'
  else if (accidentalText === 'natural') acc = 'n'
  return `${step}${acc}${octave}`
}

function readAttributes(attrsEl, state) {
  if (!attrsEl) return
  const div = attrsEl.querySelector('divisions')
  if (div?.textContent) {
    const d = parseInt(div.textContent, 10)
    if (!Number.isNaN(d) && d > 0) state.divisions = d
  }
  const fifthsEl = attrsEl.querySelector('key fifths')
  if (fifthsEl?.textContent != null) {
    const f = parseInt(fifthsEl.textContent, 10)
    if (!Number.isNaN(f)) {
      const modeEl = attrsEl.querySelector('key mode')
      state.keySignature = fifthsAndModeToKeyString(f, modeEl?.textContent?.trim())
    }
  }
  const beatsEl = attrsEl.querySelector('time beats')
  const beatTypeEl = attrsEl.querySelector('time beat-type')
  if (beatsEl?.textContent && beatTypeEl?.textContent) {
    state.timeSignature = `${beatsEl.textContent.trim()}/${beatTypeEl.textContent.trim()}`
  }
}

/** 박자 부족분을 쉼표로 채운다 */
function padWithRests(arr, beatsNeeded) {
  let rem = beatsNeeded
  for (const [dur, beats] of [['w', 4], ['h', 2], ['q', 1], ['8', 0.5]]) {
    while (rem >= beats - 0.001) {
      arr.push({ duration: toRestKey(dur), isRest: true })
      rem -= beats
    }
  }
}

/**
 * MusicXML → kordAI 내부 4성부 악보 구조
 * 각 XML 마디를 beatsPerMeasure 박자로 패딩하여 성부 간 수직 정렬을 보장한다.
 */
export function parseMusicXML(xmlString) {
  const parser = new DOMParser()
  const doc = parser.parseFromString(xmlString, 'application/xml')
  if (doc.querySelector('parseerror') || doc.querySelector('parsererror')) {
    throw new Error('XML 형식이 올바르지 않습니다.')
  }

  const scorePartwise = doc.querySelector('score-partwise') || doc.documentElement
  const partList = scorePartwise.querySelector('part-list')
  const idOrder = []
  if (partList) {
    partList.querySelectorAll('score-part').forEach((sp) => {
      idOrder.push(sp.getAttribute('id') || '')
    })
  }

  const partEls = [...scorePartwise.querySelectorAll(':scope > part')]
  if (partEls.length === 0) {
    throw new Error('part 요소가 없습니다. MusicXML partwise 파일인지 확인하세요.')
  }

  const idToVoice = { P1: 'soprano', P2: 'alto', P3: 'tenor', P4: 'bass' }
  const voices = ['soprano', 'alto', 'tenor', 'bass']
  const score = { soprano: [], alto: [], tenor: [], bass: [] }

  let globalKey = 'C'
  let globalTime = '4/4'
  // 소프라노 파트의 <direction><words>(로마숫자 화성 기호, placement="above")를
  // 마디·박 위치와 함께 수집 — kordAI가 내보낸 파일을 다시 불러올 때 분석 결과를
  // 그대로 복원하기 위함(내려받아 온 자동 재분석으로 덮어써지지 않도록 override).
  const chordSymbolHints = []

  partEls.slice(0, 4).forEach((partEl, idx) => {
    const pid = partEl.getAttribute('id') || idOrder[idx] || ''
    const voice = idToVoice[pid] ?? voices[idx]
    if (!voice) return

    let divisions = 4
    const measures = partEl.querySelectorAll(':scope > measure')

    measures.forEach((measureEl, measureIdx) => {
      const local = { divisions, keySignature: globalKey, timeSignature: globalTime }
      const measureNotes = []
      const measureNo = parseInt(measureEl.getAttribute('number'), 10) || measureIdx + 1
      let pendingChordSymbol = null
      let beatCursor = 0

      for (const child of measureEl.children) {
        const tag = (child.localName || child.tagName.replace(/^.*:/, '')).toLowerCase()
        if (tag === 'attributes') {
          readAttributes(child, local)
          divisions = local.divisions
          globalKey = local.keySignature
          globalTime = local.timeSignature
        } else if (tag === 'direction') {
          if (voice !== 'soprano') continue
          const placement = child.getAttribute('placement') || 'above'
          if (placement !== 'above') continue
          const words = child.querySelector('words')?.textContent
          if (words) pendingChordSymbol = words
        } else if (tag === 'note') {
          if (child.querySelector('chord')) continue
          const rest = child.querySelector('rest')
          const durTicks = parseInt(child.querySelector('duration')?.textContent ?? '0', 10)
          const typeName = child.querySelector('type')?.textContent
          let noteDur = 'q'
          if (durTicks > 0 && local.divisions > 0) {
            noteDur = divisionsTicksToDuration(durTicks, local.divisions)
          } else if (typeName) {
            noteDur = typeToDuration(typeName)
          }
          if (voice === 'soprano' && pendingChordSymbol != null) {
            chordSymbolHints.push({ measure: measureNo, beat: beatCursor, chordSymbol: pendingChordSymbol })
            pendingChordSymbol = null
          }
          beatCursor += BEAT_VALUES[noteDur] ?? 1
          if (rest) {
            measureNotes.push({ duration: toRestKey(noteDur), isRest: true })
          } else {
            const pitch = parsePitchFromNote(child)
            if (pitch) measureNotes.push({ pitch, duration: noteDur })
          }
        }
      }

      // 이 마디의 음표를 flat 배열에 추가
      for (const n of measureNotes) score[voice].push(n)

      // 마디가 beatsPerMeasure보다 짧으면 쉼표로 패딩 → 성부 간 수직 정렬 보장
      const bpp = parseInt(local.timeSignature.split('/')[0], 10) || 4
      const totalBeats = measureNotes.reduce((sum, n) => {
        const d = n.duration.replace('r', '')
        return sum + (BEAT_VALUES[d] ?? 1)
      }, 0)
      const remaining = bpp - totalBeats
      if (remaining > 0.001) padWithRests(score[voice], remaining)
    })
  })

  const total = voices.reduce((n, v) => n + score[v].length, 0)
  if (total === 0) {
    throw new Error('불러올 음표가 없습니다. kordAI가 인식하는 4성부(SATB) 파트가 있는 MusicXML인지 확인하세요.')
  }

  return { score, keySignature: globalKey, timeSignature: globalTime, chordSymbolHints }
}

/**
 * kordAI 4성부 악보 구조 → MusicXML
 * 내부 마디 단위(groupIntoMeasures)로 묶어 표준 MusicXML 마디를 생성한다.
 */
function escapeXml(s) {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

function isValidChordSymbolForXML(sym) {
  if (!sym || sym === '—' || sym === '-' || sym === '?') return false
  if (String(sym).startsWith('incomplete')) return false
  return true
}

function getMeasureAnalysisForXML(analysisResult, measureIndex) {
  const arr = analysisResult?.measures
  if (!arr?.length) return null
  const byNum = arr.find((m) => Number(m.measure) === measureIndex + 1)
  if (byNum) return byNum
  return arr[measureIndex] ?? null
}

function beatStarts(notes) {
  let acc = 0
  return notes.map((n) => {
    const d = n.duration.replace('r', '')
    const start = acc
    acc += BEAT_VALUES[d] ?? 1
    return start
  })
}

function directionXML(text, placement = 'above') {
  return `\n      <direction placement="${placement}">\n        <direction-type>\n          <words>${escapeXml(text)}</words>\n        </direction-type>\n      </direction>`
}

export function generateMusicXML(score, keySignature, timeSignature, tempoBpm = 120, analysisResult = null) {
  const tbpm = Math.round(Math.min(320, Math.max(20, Number(tempoBpm) || 120)))
  const { fifths, mode: keyMode } = keyToFifthsAndMode(keySignature)
  const modeXML = keyMode === 'minor' ? '<mode>minor</mode>' : ''
  const bpp = getBeatsPerMeasure(timeSignature)

  const PARTS = [
    { id: 'P1', name: 'Soprano', clefSign: 'G', clefLine: 2, stem: 'up',   key: 'soprano' },
    { id: 'P2', name: 'Alto',    clefSign: 'G', clefLine: 2, stem: 'down', key: 'alto'    },
    { id: 'P3', name: 'Tenor',   clefSign: 'F', clefLine: 4, stem: 'up',   key: 'tenor'   },
    { id: 'P4', name: 'Bass',    clefSign: 'F', clefLine: 4, stem: 'down', key: 'bass'    },
  ]

  const maxNotes = Math.max(...PARTS.map(p => score[p.key]?.length ?? 0))
  if (maxNotes === 0) return ''

  // 각 성부를 마디 단위로 그룹화
  const grouped = {}
  let maxMeasures = 1
  for (const p of PARTS) {
    grouped[p.key] = groupIntoMeasures(score[p.key] ?? [], bpp)
    maxMeasures = Math.max(maxMeasures, grouped[p.key].length)
  }

  const DUR_TICKS = { w: 16, h: 8, q: 4, '8': 2, '16': 1 }
  const TYPE_MAP  = { w: 'whole', h: 'half', q: 'quarter', '8': 'eighth', '16': 'sixteenth' }

  function noteToXML(note, stem) {
    const durKey = note.isRest ? note.duration.replace('r', '') : note.duration
    const ticks   = DUR_TICKS[durKey] ?? 4
    const typeName = TYPE_MAP[durKey] ?? 'quarter'
    if (note.isRest) {
      return `\n      <note>\n        <rest/>\n        <duration>${ticks}</duration>\n        <type>${typeName}</type>\n      </note>`
    }
    const accM = String(note.pitch).match(/^[A-G](##|#|bb|b|n)/)
    const ACC_XML_NAME = { '#': 'sharp', '##': 'double-sharp', b: 'flat', bb: 'flat-flat', n: 'natural' }
    const acc = accM?.[1] ? `\n        <accidental>${ACC_XML_NAME[accM[1]]}</accidental>` : ''
    return `\n      <note>\n        ${pitchToXML(note.pitch)}\n        <duration>${ticks}</duration>\n        <type>${typeName}</type>${acc}\n        <stem>${stem}</stem>\n      </note>`
  }

  const partList = PARTS.map(p =>
    `    <score-part id="${p.id}"><part-name>${p.name}</part-name></score-part>`
  ).join('\n')

  const partsXML = PARTS.map(p => {
    const measuresXML = Array.from({ length: maxMeasures }, (_, i) => {
      const measure = grouped[p.key][i]
      const notes = measure?.notes ?? []

      const attrs = i === 0 ? `\n      <attributes>\n        <divisions>4</divisions>\n        <key><fifths>${fifths}</fifths>${modeXML}</key>\n        <time><beats>${timeSignature.split('/')[0]}</beats><beat-type>${timeSignature.split('/')[1]}</beat-type></time>\n        <clef><sign>${p.clefSign}</sign><line>${p.clefLine}</line></clef>\n      </attributes>` : ''
      const soundTag = p.id === 'P1' && i === 0 ? `\n      <sound tempo="${tbpm}"/>` : ''

      // 음표 XML 생성
      const noteXMLs = notes.map(n => noteToXML(n, p.stem))

      // 소프라노 파트에 화성 분석 결과(로마숫자/코드 기호)를 <direction><words>로 삽입
      if (p.id === 'P1' && analysisResult && notes.length > 0) {
        const aMeasure = getMeasureAnalysisForXML(analysisResult, i)
        const beats = aMeasure?.beats
        if (beats?.length) {
          const starts = beatStarts(notes)
          const beatNums = beats.map((b) => Number(b?.beat)).filter((v) => Number.isFinite(v))
          const beatBaseOffset = beatNums.length > 0 && Math.min(...beatNums) >= 1 ? 1 : 0
          let prevSym = null
          for (const b of beats) {
            if (!isValidChordSymbolForXML(b?.chordSymbol)) continue
            if (b.chordSymbol === prevSym) continue
            prevSym = b.chordSymbol
            const raw = Number(b.beat)
            if (!Number.isFinite(raw)) continue
            const beatPos = Math.min(bpp - 0.0001, Math.max(0, raw - beatBaseOffset))
            let idx = 0
            for (let k = 0; k < starts.length; k++) {
              if (starts[k] <= beatPos + 0.001) idx = k
              else break
            }
            noteXMLs[idx] = directionXML(b.chordSymbol) + noteXMLs[idx]
          }
        } else if (isValidChordSymbolForXML(aMeasure?.chordSymbol)) {
          noteXMLs[0] = directionXML(aMeasure.chordSymbol) + noteXMLs[0]
        }
      }

      // 4성부 전체에 비화성음(NCT: PT/UN/... 등) 표시 — 해당 성부 음표 아래에 <direction>으로 삽입
      if (analysisResult && notes.length > 0) {
        const aMeasureNct = getMeasureAnalysisForXML(analysisResult, i)
        const beatsNct = aMeasureNct?.beats
        if (beatsNct?.length) {
          const starts = beatStarts(notes)
          const beatNums = beatsNct.map((b) => Number(b?.beat)).filter((v) => Number.isFinite(v))
          const beatBaseOffset = beatNums.length > 0 && Math.min(...beatNums) >= 1 ? 1 : 0
          for (const b of beatsNct) {
            const nctType = b?.nct?.[p.key]
            if (!nctType) continue
            const rawStart = Number(b?.nctStart?.[p.key])
            const raw = Number.isFinite(rawStart) ? rawStart : Number(b.beat) - beatBaseOffset
            if (!Number.isFinite(raw)) continue
            const beatPos = Math.min(bpp - 0.0001, Math.max(0, raw))
            let idx = 0
            for (let k = 0; k < starts.length; k++) {
              if (starts[k] <= beatPos + 0.001) idx = k
              else break
            }
            noteXMLs[idx] = directionXML(nctType, 'below') + noteXMLs[idx]
          }
        }
      }

      // 마디가 짧으면 쉼표 패딩
      let beatsFilled = notes.reduce((s, n) => {
        const d = n.duration.replace('r', '')
        return s + (BEAT_VALUES[d] ?? 1)
      }, 0)
      let rem = bpp - beatsFilled
      if (rem > 0.001) {
        const padNotes = []
        padWithRests(padNotes, rem)
        padNotes.forEach(n => noteXMLs.push(noteToXML(n, p.stem)))
      }

      // 완전히 빈 마디 → 온음 쉼표
      if (noteXMLs.length === 0) {
        noteXMLs.push(`\n      <note>\n        <rest/>\n        <duration>16</duration>\n        <type>whole</type>\n      </note>`)
      }

      return `    <measure number="${i + 1}">${attrs}${soundTag}${noteXMLs.join('')}\n    </measure>`
    }).join('\n')

    return `  <part id="${p.id}">\n${measuresXML}\n  </part>`
  }).join('\n')

  return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE score-partwise PUBLIC "-//Recordare//DTD MusicXML 3.1 Partwise//EN" "http://www.musicxml.org/dtds/partwise.dtd">
<score-partwise version="3.1">
  <part-list>
${partList}
  </part-list>
${partsXML}
</score-partwise>`
}
